import {
  Avatar,
  AvatarFallback,
  Button,
  getInitials,
} from '@/components/health-ui';
import { createTestIdHelper } from '@/lib/utils';
import { KeyRound, LogOut, RefreshCw, ShieldCheck } from 'lucide-react';

export type AccountAccessShellProps = {
  displayName: string;
  email?: string;
  accountUrl: string;
  onRefreshAccess: () => void;
  onSignOut: () => void;
};

export const accountAccessShellTestId = createTestIdHelper(
  'account-access-shell',
);

/**
 * Nonclinical landing surface for an authenticated Staff Account that has no
 * application authority. It intentionally has no API-provider dependency.
 */
export function AccountAccessShell({
  displayName,
  email,
  accountUrl,
  onRefreshAccess,
  onSignOut,
}: Readonly<AccountAccessShellProps>): React.JSX.Element {
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-canvas px-4 py-8 text-foreground"
      data-testid={accountAccessShellTestId()}
    >
      <section className="w-full max-w-2xl overflow-hidden rounded-lg border border-border bg-background">
        <header className="flex items-center gap-3 border-b border-border px-5 py-4 sm:px-6">
          <img
            src="/logo-mark.svg"
            alt="Medintelekt"
            className="size-9 rounded-md"
          />
          <div className="min-w-0">
            <p className="font-display text-base font-semibold">Medintelekt</p>
            <p className="text-sm text-muted-foreground">Account access</p>
          </div>
        </header>

        <div className="px-5 py-7 sm:px-8 sm:py-9">
          <div className="mb-5 inline-flex items-center gap-2 rounded-md bg-muted px-2.5 py-1.5 text-sm font-medium">
            <ShieldCheck className="size-4" aria-hidden="true" />
            No application access assigned
          </div>

          <h1 className="max-w-[26ch] text-balance font-display text-2xl font-semibold tracking-tight">
            Your account is ready, but access has not been assigned
          </h1>
          <p className="mt-3 max-w-[65ch] text-pretty text-sm leading-6 text-muted-foreground">
            You can manage your sign-in credentials here. Patient and clinical
            information stays unavailable until an access administrator assigns
            an approved profile and scope.
          </p>

          <dl className="mt-7 divide-y divide-border border-y border-border text-sm">
            <div className="flex items-center gap-3 py-3.5">
              <Avatar size="sm">
                <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <dt className="text-xs font-medium text-muted-foreground">
                  Signed-in account
                </dt>
                <dd className="truncate font-semibold">{displayName}</dd>
                {email && email !== displayName ? (
                  <dd className="truncate text-muted-foreground">{email}</dd>
                ) : null}
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 py-3.5">
              <dt className="text-muted-foreground">Account type</dt>
              <dd className="font-semibold">Staff</dd>
            </div>
            <div className="flex items-center justify-between gap-4 py-3.5">
              <dt className="text-muted-foreground">Clinical data access</dt>
              <dd className="font-semibold">Not assigned</dd>
            </div>
          </dl>

          <div className="mt-7 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
            <Button asChild>
              <a href={accountUrl}>
                <KeyRound className="size-4" aria-hidden="true" />
                Manage credentials
              </a>
            </Button>
            <Button variant="outline" onClick={onRefreshAccess}>
              <RefreshCw className="size-4" aria-hidden="true" />
              Check access again
            </Button>
            <Button variant="ghost" onClick={onSignOut}>
              <LogOut className="size-4" aria-hidden="true" />
              Sign out
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
